import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { filter, first, map, switchMap } from "rxjs/operators";
import { LoginService } from "src/app/main/authentication/login/login.service";
import { ProfileService } from "src/app/main/profile/profile.service";

@Injectable({
  providedIn: "root",
})
export class AuthAccountGuard {
  /**
   * Constructor
   */
  constructor(private loginService: LoginService, private _router: Router, private profileService: ProfileService) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this._check();
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._check();
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this._check();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @private
   */
  private _check(): Observable<boolean> {
    // Check the authentication status
    return this.loginService.isAuthenticated().pipe(
      switchMap((authStatus) => {
        if (authStatus.isAuth) {
          return this.profileService.currentUserProfile.pipe(
            filter(p => !!p),
            first(),
            map(profile => ({
              isAuth: true,
              emailVerified: profile.emailVerified || authStatus.emailVerified,
              mobileVerified: profile.mobileVerified || authStatus.mobileVerified,
              birthDate: profile.birthDate || profile.user?.birthDate || '',
              address: profile.address || profile.data?.address || '',
              profileVerified: profile.profileVerified
            }))
          );
        }
        return of(authStatus);
      }),
      switchMap((authenticated) => {

        // Check if the address or birth date is reset for testing purposes
        // const isResetAddress = authenticated.address === 'TEST_ADDRESS_RESET';
        // const isResetBirthDate = authenticated.birthDate === '1901-01-01';
        // If the user is not authenticated...
        if (authenticated.isAuth == false || authenticated.emailVerified == undefined) {
          // Redirect to the sign-in page
          this._router.navigate(["/auth/login"]);

          // Prevent the access
          return of(false);
        } else if (authenticated.emailVerified == false) {
          this._router.navigate(["/auth/verify-email"]);
          // Prevent the access
          return of(false);
        } else if (authenticated.mobileVerified == false) {
          this._router.navigate(["/auth/verify-mobile"]);
          // Prevent the access
          return of(false);
          // add || isResetAddress || isResetBirthDate to the condition for testing purposes
        } else if (!authenticated.birthDate || !authenticated.address) {
          this._router.navigate(["/auth/complete-profile"]);
          // Prevent the access
          return of(false);
        }
        // Commented for DIGITAL ID remove
        // else if (
        //   authenticated.profileVerified === 0 ||
        //   authenticated.profileVerified === 1
        // ) {
        //   this._router.navigate(["/auth/id-check"]);
        //   // Prevent the access
        //   return of(false);
        // }
        // Allow the access
        return of(true);
      })
    );
  }
}
